import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
	theme: Theme;
	toggleTheme: () => void;
	setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

interface ThemeProviderProps {
	children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
	const [theme, setTheme] = useState<Theme>(() => {
		// Check if there's a saved theme in localStorage
		const savedTheme = localStorage.getItem('theme') as Theme;
		if (savedTheme) {
			return savedTheme;
		}

		// Check system preference
		if (globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches) {
			return 'dark';
		}

		return 'light';
	});

	useEffect(() => {
		// Use requestAnimationFrame for smoother DOM updates
		const rafId = requestAnimationFrame(() => {
			const root = globalThis.document.documentElement;

			// Remove existing theme classes
			root.classList.remove('light', 'dark');

			// Add current theme class
			root.classList.add(theme);
		});

		// Defer localStorage write to not block rendering
		const timeoutId = setTimeout(() => {
			localStorage.setItem('theme', theme);
		}, 0);

		return () => {
			cancelAnimationFrame(rafId);
			clearTimeout(timeoutId);
		};
	}, [theme]);

	useEffect(() => {
		// Listen for system theme changes
		const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');

		const handleChange = (e: MediaQueryListEvent) => {
			const savedTheme = localStorage.getItem('theme');
			if (!savedTheme) {
				setTheme(e.matches ? 'dark' : 'light');
			}
		};

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	const toggleTheme = () => {
		setTheme(theme === 'light' ? 'dark' : 'light');
	};

	const value = React.useMemo(
		() => ({
			theme,
			toggleTheme,
			setTheme
		}),
		[theme]
	);

	return (
		<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
	);
};
